/*
 * Author: Ben Zhao
 * File Created: Tuesday, 24-11-26 10:47 am
 * Last Modified: Thursday, 25-03-06 11:11 am, by Ben Zhao
 */

import { create } from 'zustand';

import { cookies, createStoreActions, generateUseShallow } from '@web/utils';

import type { Currency } from '@/@types/asset';

// eslint-disable-next-line import/no-cycle -- ignore
import { getCurrencyRatioState } from './useCurrencyRatioStore';

interface IState {
  /** 是否隐藏用户资产 */
  isPrivacy: boolean;
  /** 当前用户选择的货币单位，默认为SGD */
  selectedCurrency: Currency;
  /** 正在请求汇率 */
  isRatioRequesting?: boolean;
}

interface IAction {
  changeCurrency: (
    _currency: Currency,
    _noStateUpdate?: boolean,
  ) => Promise<void>;
  changePrivacy: (_isPrivacy: boolean, _noStateUpdate?: boolean) => void;
  init: (_s?: Partial<IState>) => void;
}

const defaultState: IState = {
  isPrivacy: false,
  selectedCurrency: 'SGD',
};

const actions = createStoreActions<IState, IAction>((set, get) => ({
  async changeCurrency(currency, noStateUpdate) {
    const { isRatioRequesting, selectedCurrency } = get();

    if (isRatioRequesting || selectedCurrency === currency) {
      return;
    }

    set({
      isRatioRequesting: true,
    });
    if (currency !== 'USD') {
      await getCurrencyRatioState().fetchCurrencyRatio(currency);
      cookies.set('selected-currency', currency);
      if (!noStateUpdate) {
        set({ selectedCurrency: currency });
      }
    } else {
      cookies.set('selected-currency', currency);
      set({
        selectedCurrency: currency,
      });
    }

    set({
      isRatioRequesting: false,
    });
  },
  changePrivacy(isPrivacy: boolean, noStateUpdate?: boolean) {
    cookies.set('show-asset', isPrivacy ? 'N' : 'Y');
    if (!noStateUpdate) {
      set({ isPrivacy });
    }
  },
  init(s?: Partial<IState>) {
    const { isPrivacy, selectedCurrency } = s || {};
    const { changeCurrency, changePrivacy } = get();

    const isPrivacyCur = cookies.get('show-asset') !== 'Y';
    const selectedCurrencyCur = (cookies.get('selected-currency') ||
      'SGD') as Currency;

    changePrivacy(isPrivacy ?? isPrivacyCur);
    changeCurrency(selectedCurrency ?? selectedCurrencyCur).catch(() => {
      // noop
    });
  },
}));

const useUserAssetStore = create<IState & IAction>()((set, get) => ({
  ...defaultState,
  ...actions(set, get),
}));

/** store 功能聚合导出 */
export const useUserAsset = generateUseShallow<IState, IAction>(
  useUserAssetStore,
);

/** 函数式获取 store 的状态 */
export const getUserAsset = useUserAssetStore.getState;
